import styled from "@emotion/styled";
import { FC, useState, useEffect } from "react";
import { Button, Grid, Box, Table, TableHead, TableCell, TableRow } from "@mui/material"
import { FinancialRatioMapList, SystemErrorMsg, SystemErrorTitle } from "../../constants";
import { useNavigate, useLocation } from 'react-router-dom';
import { financialAnalysisMastersByMainCategory } from '../../graphql/queries';
import { generateClient } from 'aws-amplify/api';
import { industryData } from "pages/selectIndustry/IndustryData";
import "./DisplayFinancialRatio.css";
import { sessionDisconnectedCheck } from "shared/sessionDisconnectedCheck";
import createPdf from "shared/createPdf";
import { BackdropLoading } from "shared/backdropLoading";

const HeaderLabel = styled.header`
    background-color: #41DBCE;
    color: #FFFFFF;
    height: 3rem;
    font-size: 1.5rem;
    padding-top: 0.5rem;
    padding-left: 2rem;
`;


const CenterGrid = styled(Grid)(({ }) => ({
    backgroundColor: '#f5f5f5',
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    height: '105rem',
    lineHeight: '2.3rem',
    padding: '0.5rem',
    marginTop: '1.5rem',
    marginBottom: '1rem',
}));

const TableGrid = styled(Grid)(({ }) => ({
    backgroundColor: '#ffffff',
    textAlign: 'center',
    height: '100rem',
    width: '84%',
    padding: '1.5rem',
    marginTop: '2rem',
    marginBottom: '5rem',
    marginRight: '5rem',
    marginLeft: '5rem',
}));

const AppliedButton = styled(Button)(({ }) => ({
    backgroundColor: '#01CFBE',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1.0rem',
    height: '2.1rem',
    width: '15rem',
    borderRadius: '100rem',
}));

const ReturnbuttonArea = styled(Box)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1rem',
    height: '4rem',
    lineHeight: '4rem',
    marginTop: '0.5rem',
    marginBottom: '3rem',
}));

const buttonStyle = { marginBottom: '1rem' };

const screenId = 'U031'

const DisplayFinancialRatio: FC = () => {
    const navigate = useNavigate();
    const location = useLocation()
    const [loading, setLoading] = useState<boolean>(true);
    const [searchingList, setSearchingList] = useState<boolean>(true);
    const [searchingListB, setSearchingListB] = useState<boolean>(true);
    const [searchingList2, setSearchingList2] = useState<boolean>(true);
    const [searchingList2B, setSearchingList2B] = useState<boolean>(true);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", "", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if ([searchingList, searchingListB, searchingList2, searchingList2B].every(value => !value)) {
            setLoading(false)
        }
    }, [searchingList, searchingListB, searchingList2, searchingList2B])

    const downloadPDF = async () => {
        setLoading(true)
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "downloadPDF", location.state.yearEdition, navigate)) {
            return
        }
        let result: any = {}
        result = await callCreatePdf()
        setLoading(false)
    };

    const [selectIndustry] = useState<{ industryData: industryData, industryData2: industryData, yearEdition: string }>(location.state as { industryData: industryData, industryData2: industryData, yearEdition: string })
    const [list1, setList1] = useState<any[]>([])
    const [list1B, setList1B] = useState<any[]>([])
    const [list2, setList2] = useState<any[]>([])
    const [list2B, setList2B] = useState<any[]>([])

    const onClickSearchFinancialRatioPage = async () => {
        if (await sessionDisconnectedCheck(screenId, "onClickSearchFinancialRatioPage", location.state.yearEdition, navigate)) {
            return
        }
        navigate("/SearchFinancialRatio", { state: location.state });
    };

    if (selectIndustry.industryData.scaleCode == "blank") selectIndustry.industryData.scaleCode = ""
    if (selectIndustry.industryData2.scaleCode == "blank") selectIndustry.industryData2.scaleCode = ""
    if (selectIndustry.industryData.subCategory == "blank") selectIndustry.industryData.subCategory = ""
    if (selectIndustry.industryData2.subCategory == "blank") selectIndustry.industryData2.subCategory = ""
    if (selectIndustry.industryData.item == "blank") selectIndustry.industryData.item = ""
    if (selectIndustry.industryData2.item == "blank") selectIndustry.industryData2.item = ""

    useEffect(() => {
        if (!selectIndustry.industryData.mainCategory) {
            setSearchingList(false)
            return
        }
        (async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "getAverageList1", location.state.yearEdition, navigate)) {
                return
            }
        })()
        const getList1 = async () => {
            try {
                let allItems: any[] = [];
                let nextToken: string | null = null;
                const client = generateClient();
                do {
                    const result: any = await client.graphql({
                        query: financialAnalysisMastersByMainCategory,
                        variables: {
                            limit: 1000,
                            nextToken: nextToken,
                            mainCategory: selectIndustry.industryData.mainCategory,
                            filter: {
                                and: [
                                    { subCategory: { eq: selectIndustry.industryData.subCategory } },
                                    { item: { eq: selectIndustry.industryData.item } },
                                    { scaleCode: { eq: selectIndustry.industryData.scaleCode } },
                                    { yearEdition: { eq: selectIndustry.yearEdition } },
                                    { surplus: { eq: "" } },
                                ]
                            }
                        },
                    });
                    const items = result.data?.financialAnalysisMastersByMainCategory?.items || [];
                    nextToken = result.data?.financialAnalysisMastersByMainCategory?.nextToken || null;
                    allItems = allItems.concat(items)
                } while (nextToken)
                setList1(allItems)
                setSearchingList(false)
            } catch (err) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        getList1()
    }, [selectIndustry.industryData.mainCategory]);

    useEffect(() => {
        if (!selectIndustry.industryData.mainCategory) {
            setSearchingListB(false)
            return
        }
        (async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "getSurplusList1", location.state.yearEdition, navigate)) {
                return
            }
        })()
        const getList1B = async () => {
            try {
                let allItems: any[] = [];
                let nextToken: string | null = null;
                const client = generateClient();
                do {
                    const result: any = await client.graphql({
                        query: financialAnalysisMastersByMainCategory,
                        variables: {
                            limit: 1000,
                            nextToken: nextToken,
                            mainCategory: selectIndustry.industryData.mainCategory,
                            filter: {
                                and: [
                                    { subCategory: { eq: selectIndustry.industryData.subCategory } },
                                    { item: { eq: selectIndustry.industryData.item } },
                                    { scaleCode: { eq: selectIndustry.industryData.scaleCode } },
                                    { yearEdition: { eq: selectIndustry.yearEdition } },
                                    { surplus: { eq: "1" } },
                                ]
                            }
                        },
                    });
                    const items = result.data?.financialAnalysisMastersByMainCategory?.items || [];
                    nextToken = result.data?.financialAnalysisMastersByMainCategory?.nextToken || null;
                    allItems = allItems.concat(items)
                } while (nextToken)
                setList1B(allItems)
                setSearchingListB(false)
            } catch (err) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        getList1B()
    }, [selectIndustry.industryData.mainCategory]);


    useEffect(() => {
        if (!selectIndustry.industryData2.mainCategory) {
            setSearchingList2(false)
            return
        }
        (async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "getAverageList2", location.state.yearEdition, navigate)) {
                return
            }
        })()
        const getList2 = async () => {
            try {
                let allItems: any[] = [];
                let nextToken: string | null = null;
                const client = generateClient();
                do {
                    const result: any = await client.graphql({
                        query: financialAnalysisMastersByMainCategory,
                        variables: {
                            limit: 1000,
                            nextToken: nextToken,
                            mainCategory: selectIndustry.industryData2.mainCategory,
                            filter: {
                                and: [
                                    { subCategory: { eq: selectIndustry.industryData2.subCategory } },
                                    { item: { eq: selectIndustry.industryData2.item } },
                                    { scaleCode: { eq: selectIndustry.industryData2.scaleCode } },
                                    { yearEdition: { eq: selectIndustry.yearEdition } },
                                    { surplus: { eq: "" } },
                                ]
                            }
                        },
                    });
                    const items = result.data?.financialAnalysisMastersByMainCategory?.items || [];
                    nextToken = result.data?.financialAnalysisMastersByMainCategory?.nextToken || null;
                    allItems = allItems.concat(items)
                } while (nextToken)
                setList2(allItems)
                setSearchingList2(false)
            } catch (err) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        getList2()
    }, [selectIndustry.industryData2.mainCategory]);

    useEffect(() => {
        if (!selectIndustry.industryData2.mainCategory) {
            setSearchingList2B(false)
            return
        }
        (async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "getSurplusList2", location.state.yearEdition, navigate)) {
                return
            }
        })()
        const getList2B = async () => {
            try {
                let allItems: any[] = [];
                let nextToken: string | null = null;
                const client = generateClient();
                do {
                    const result: any = await client.graphql({
                        query: financialAnalysisMastersByMainCategory,
                        variables: {
                            limit: 1000,
                            nextToken: nextToken,
                            mainCategory: selectIndustry.industryData2.mainCategory,
                            filter: {
                                and: [
                                    { subCategory: { eq: selectIndustry.industryData2.subCategory } },
                                    { item: { eq: selectIndustry.industryData2.item } },
                                    { scaleCode: { eq: selectIndustry.industryData2.scaleCode } },
                                    { yearEdition: { eq: selectIndustry.yearEdition } },
                                    { surplus: { eq: "1" } },
                                ]
                            }
                        },
                    });
                    const items = result.data?.financialAnalysisMastersByMainCategory?.items || [];
                    nextToken = result.data?.financialAnalysisMastersByMainCategory?.nextToken || null;
                    allItems = allItems.concat(items)
                } while (nextToken)
                setList2B(allItems)
                setSearchingList2B(false)
            } catch (err) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        getList2B()
    }, [selectIndustry.industryData2.mainCategory]);

    // 財務分析計算APIの呼び出し
    const callCreatePdf = async () => {
        let request = {
            mainCategory: location.state.industryData.mainCategory,
            industryName: location.state.industryData.industryName,
            industryName2: location.state.industryData2.industryName,
            outputTarget: "CreateFinancialRatioPdf",
            standardFinancialRatios: Object.fromEntries(Object.entries(list1[0] ?? {}).filter(([key]) =>
                FinancialRatioMapList.some(item => item.idx === key))),
            standardFinancialRatiosSurplus: Object.fromEntries(Object.entries(list1B[0] ?? {}).filter(([key]) =>
                FinancialRatioMapList.some(item => item.idx === key))),
            standardFinancialRatios2: Object.fromEntries(Object.entries(list2[0] ?? {}).filter(([key]) =>
                FinancialRatioMapList.some(item => item.idx === key))),
            standardFinancialRatios2Surplus: Object.fromEntries(Object.entries(list2B[0] ?? {}).filter(([key]) =>
                FinancialRatioMapList.some(item => item.idx === key)))
        }
        const result: any = await createPdf(request)
        // 帳票出力APIから正常終了以外が返却されたらエラー表示
        if (result != "I0000") {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
        return result
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <HeaderLabel>
                標準財務比率
            </HeaderLabel>
            <CenterGrid>
                <TableGrid>
                    <Grid sx={{ textAlign: 'right' }}>
                        <AppliedButton variant="contained" color="secondary" style={buttonStyle} onClick={() => downloadPDF()}><img src={`${process.env.PUBLIC_URL}/Icons/downloadIcon.svg`} width="13px" />&nbsp;&nbsp;PDFダウンロード</AppliedButton>
                    </Grid>
                    <Table sx={{ border: 'none' }}>
                        <TableHead>
                            <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid black', height: '1.5rem', fontSize: '1,0rem', paddingLeft: '0', paddingRight: '0', paddingTop: '0.2rem', paddingBottom: '0.2rem', textAlign: 'center' } }}>
                                <TableCell className="industryColums" rowSpan={3}>項目</TableCell>
                                {selectIndustry.industryData.industryName != "" ? <TableCell className="industryColums" colSpan={2}>{selectIndustry.industryData.industryName}</TableCell> : <TableCell className="industryColums" colSpan={2}></TableCell>}
                                {selectIndustry.industryData2.industryName != "" ? <TableCell className="industryColums" colSpan={2}>{selectIndustry.industryData2.industryName}</TableCell> : <TableCell className="industryColums" colSpan={2}></TableCell>}
                            </TableRow>
                            <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid black', height: '1.5rem', fontSize: '1.0rem', paddingLeft: '0', paddingRight: '0', paddingTop: '0.2rem', paddingBottom: '0.2rem', textAlign: 'center' } }}>
                                <TableCell className="averageColums">総平均</TableCell>
                                <TableCell className="averageColums">黒字企業平均</TableCell>
                                <TableCell className="averageColums">総平均</TableCell>
                                <TableCell className="averageColums">黒字企業平均</TableCell>
                            </TableRow>
                        </TableHead>
                        {FinancialRatioMapList.map((row) => (
                            <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid black', height: '0.3rem', fontSize: '0.8rem', paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0', paddingBottom: '0', } }}>
                                <TableCell sx={{ textAlign: 'left' }}>{row.column}</TableCell>
                                {selectIndustry.industryData.industryName != "" && list1[0] ? <TableCell sx={{ textAlign: 'right' }}>{list1[0][row?.idx]}</TableCell> : <TableCell></TableCell>}
                                {selectIndustry.industryData.industryName != "" && list1B[0] ? <TableCell sx={{ textAlign: 'right' }}>{list1B[0][row?.idx]}</TableCell> : <TableCell></TableCell>}
                                {selectIndustry.industryData2.industryName != "" && list2[0] ? <TableCell sx={{ textAlign: 'right' }}>{list2[0][row?.idx]}</TableCell> : <TableCell></TableCell>}
                                {selectIndustry.industryData2.industryName != "" && list2B[0] ? <TableCell sx={{ textAlign: 'right' }}>{list2B[0][row?.idx]}</TableCell> : <TableCell></TableCell>}
                            </TableRow>
                        ))}
                    </Table>
                </TableGrid>
            </CenterGrid>
            <ReturnbuttonArea>
                <div>
                    <span onClick={onClickSearchFinancialRatioPage} style={{ cursor: 'pointer' }}>
                        <img src={`${process.env.PUBLIC_URL}/Icons/returnIcon.svg`} width="13px" />&nbsp;&nbsp;戻る
                    </span>
                </div>
            </ReturnbuttonArea>
        </>
    );
}

export default DisplayFinancialRatio;
